import { DictionaryItem } from 'api-new/generated';
import type { GenderValue } from 'shared';
import * as yup from 'yup';

export const applicantRoleValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-applicant-role', 'Applicant Role is required', (value) => Boolean(value?.id));

export const applicantTypeValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-applicant-type', 'Applicant Type is required', (value) => Boolean(value?.id));

export const employmentTypeValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-employment-type', 'Employment Type is required', (value) => Boolean(value?.id));

export const employmentProfessionValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-employment-profession', 'Profession is required', (value) => Boolean(value?.id));

export const employmentIndustryValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-employment-industry', 'Employer Industry is required', (value) => Boolean(value?.id));

export const employmentEmployerValidation = yup.string().trim().required('Employer is required');

export const employmentDateBeginValidation = yup
  .date()
  .required('Start Time is required')
  .max(new Date(), 'Start Time cannot be in the future');

export const titleValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-title', 'Title is required', (value) => Boolean(value?.id));

export const maritalStatusValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-marital-status', 'Marital Status is required', (value) => Boolean(value?.id));

export const residentialStatusValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-residential-status', 'Residential Status is required', (value) => Boolean(value?.id));

export const numberOfDependentsValidation = yup
  .mixed<number>()
  .nullable()
  .test(
    'required-number-of-dependents',
    'Number of Dependents is required',
    (value) => value !== undefined && value !== null
  );

export const nationalityValidation = yup
  .mixed<DictionaryItem>()
  .nullable()
  .test('required-nationality', 'Nationality is required', (value) => Boolean(value?.id));

export const genderValidation = yup
  .mixed<GenderValue>()
  .nullable()
  .oneOf(['male', 'female'], 'Gender is required')
  .required('Gender is required');

export const firstNameValidation = yup.string().trim().required('First Name is required');

export const surnameValidation = yup.string().trim().required('Surname is required');

export const dobValidation = yup
  .date()
  .required('Date of Birth is required')
  .max(new Date(), 'Date of Birth cannot be in the future');

export const startTimeValidation = yup
  .date()
  .required('Start Time is required')
  .max(new Date(), 'Start Time cannot be in the future');

export const bankAccountNumberValidation = yup.string().trim().required('Bank Account Number is required');

export const bankSortCodeValidation = yup
  .string()
  .trim()
  .required('Bank Sort Code is required')
  .test('is-valid-bank-sort-code', 'Bank Sort Code is required', (value) => {
    if (!value) return false;

    return /^\d{2}-\d{2}-\d{2}$/.test(value);
  });

export const phoneValidation = yup
  .string()
  .required('Phone number is required')
  .trim()
  .matches(/^(0\d{10}|\+44\d{10})$/, 'Phone number is not valid. Valid formats: 0XXXXXXXXXX or +44XXXXXXXXXX');

const phoneNumberValidation = (requiredMessage: string, lengthMessage: string) =>
  yup
    .string()
    .trim()
    .required(requiredMessage)
    .matches(/^\d{11}$/, lengthMessage);

export const phoneMobileValidation = phoneNumberValidation(
  'Phone Mobile is required',
  'Phone Mobile must be 11 digits'
);

export const createPhoneHomeValidation = (
  requiredMessage = 'Phone Home is required',
  lengthMessage = 'Phone Home must be 11 digits'
) => phoneNumberValidation(requiredMessage, lengthMessage);

export const phoneHomeValidation = createPhoneHomeValidation();

export const phoneWorkValidation = yup
  .string()
  .trim()
  .test('is-valid-phone-work', 'Phone Work must be 11 digits', (value) => {
    if (!value || value.length === 0) return true;
    return /^\d{11}$/.test(value);
  });

export const emailAddressValidation = yup
  .string()
  .required('Email Address is required')
  .test('is-valid-email', 'Email Address is required', (value) => {
    if (!value) return false;
    const re = /\S+@\S+\.\S{2,}/;
    return re.test(value);
  });

export type AddressValidationField =
  | 'postcode'
  | 'flat'
  | 'buildingNumber'
  | 'buildingName'
  | 'street'
  | 'suburb'
  | 'city'
  | 'county';

const ADDRESS_VALIDATION_SCHEMA: Record<AddressValidationField, yup.StringSchema> = {
  postcode: yup.string().trim().required('Postcode is required'),
  flat: yup.string().trim(),
  buildingNumber: yup.string().trim(),
  buildingName: yup.string().trim(),
  street: yup.string().trim().required('Street is required'),
  suburb: yup.string().trim().required('Suburb is required'),
  city: yup.string().trim().required('City is required'),
  county: yup.string().trim().required('County is required')
};

export const buildAddressValidation = (excludeFields: AddressValidationField[] = []) => {
  const shape = Object.entries(ADDRESS_VALIDATION_SCHEMA).reduce<
    Partial<Record<AddressValidationField, yup.StringSchema>>
  >((acc, [field, schema]) => {
    if (!excludeFields.includes(field as AddressValidationField)) {
      acc[field as AddressValidationField] = schema;
    }

    return acc;
  }, {});

  return yup.object().shape(shape);
};

export const addressValidation = buildAddressValidation();
